/* Cabinet · «Рабочая тетрадь»
 * List + detail of saved workbook entries.
 * Two entry types render distinctly:
 *   • recipe_calculation      → opens the read-only detail drawer here.
 *   • product_cost_calculation → navigates to /master-order/product-cost?entry=…
 *                                (the same editor page loads the state back).
 */
import React, { useEffect, useState, useCallback } from "react";
import { NotebookPen, X, ChevronRight, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";
import { listWorkbookEntries, getWorkbookEntry, deleteWorkbookEntry } from "@/lib/workbookApi";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
};
const fmtNum = (v, digits = 1) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const r = n >= 10 ? Math.round(n) : Number(n.toFixed(digits));
  return String(r).replace(".", ",");
};
const fmtMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(2).replace(".", ",");
};

const shapeLabel = (s) => {
  if (!s || !s.shape) return null;
  if (s.shape === "round" && s.diameter) return `Круг Ø${fmtNum(s.diameter, 0)}`;
  if (s.shape === "square" && s.side) return `Квадрат ${fmtNum(s.side, 0)}×${fmtNum(s.side, 0)}`;
  if (s.shape === "rect" && s.length && s.width) return `Прямоуг. ${fmtNum(s.length, 0)}×${fmtNum(s.width, 0)}`;
  return s.shape;
};

const shapeSummary = (payload) => {
  const from = shapeLabel(payload?.source_shape);
  const to = shapeLabel(payload?.target_shape);
  if (from && to && from !== to) return `${from} → ${to}`;
  return to || from || null;
};

const heightHint = (payload) => {
  const t = payload?.target_shape;
  if (t && t.height) return `Высота ${fmtNum(t.height, 1)} см`;
  const s = payload?.source_shape;
  if (s && s.height) return `Высота ${fmtNum(s.height, 1)} см`;
  return null;
};

export default function WorkbookSection() {
  const [entries, setEntries] = useState(null);
  const [detail, setDetail] = useState(null); // { entry }
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      // Load ALL workbook types so recipe & product-cost cards coexist.
      const list = await listWorkbookEntries();
      setEntries(list);
    } catch { setEntries([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const openEntry = async (summary) => {
    // Both types open in the in-cabinet read-only drawer. Product-cost
    // entries live in the user's notebook and MUST stay accessible even
    // when the user has lost or never had access to Master Order.
    setDetail({ loading: true });
    try {
      const entry = await getWorkbookEntry(summary.id);
      setDetail({ entry });
    } catch { setDetail(null); }
  };

  const handleDeleted = useCallback((deletedId) => {
    setEntries((prev) => (prev || []).filter((e) => e.id !== deletedId));
    setDetail(null);
  }, []);

  return (
    <section data-testid="account-workbook" className="pb-8">
      <header className="flex items-baseline justify-between mb-4">
        <h2 className="font-serif text-2xl text-espresso">Рабочая тетрадь</h2>
        {entries && entries.length > 0 && (
          <span className="text-xs uppercase tracking-widest text-espresso/60"
                data-testid="wb-count">{entries.length}</span>
        )}
      </header>

      {loading && entries === null && (
        <div className="text-sm text-espresso/70">Загружаем…</div>
      )}

      {entries !== null && entries.length === 0 && (
        <div className="rounded-2xl p-6 text-center" style={{ background: "#FBF3E4", color: "#5B4326" }}
             data-testid="wb-empty">
          <NotebookPen size={28} className="mx-auto mb-3 opacity-70" />
          <div className="text-[15px] font-medium">В Рабочей тетради пока нет записей</div>
          <div className="text-[13px] mt-1" style={{ color: "#8B6F4B" }}>
            Сохраните рецепт или расчёт из инструментов Anokina Studio — он появится здесь.
          </div>
        </div>
      )}

      {entries && entries.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-2">
          {entries.map((e) => (
            <WorkbookCard key={e.id} summary={e} onOpen={() => openEntry(e)} />
          ))}
        </div>
      )}

      {detail && (
        <WorkbookDetailDrawer
          state={detail}
          onClose={() => setDetail(null)}
          onDeleted={handleDeleted}
        />
      )}
    </section>
  );
}

function WorkbookCard({ summary, onOpen }) {
  const isProduct = summary.type === "product_cost_calculation";
  return (
    <button onClick={onOpen}
            className="text-left rounded-2xl p-4 bg-white transition hover:shadow-md"
            style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}
            data-testid={isProduct ? "wb-card-product" : "wb-card"}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1 flex items-center gap-1"
               style={{ color: "#8B6F4B" }}>
            {isProduct ? <Layers size={11} /> : <NotebookPen size={11} />}
            {isProduct ? "Расчёт изделия" : "Рецепт · Расчёт"}
          </div>
          <div className="text-[16px] leading-snug text-espresso truncate">{summary.title}</div>
          {isProduct && Number.isFinite(Number(summary.subtotal)) && (
            <div className="text-[12.5px] mt-1 tabular-nums" style={{ color: "#5B4326" }}
                 data-testid="wb-card-subtotal">
              Себестоимость компонентов: {fmtMoney(summary.subtotal)} {summary.currency_code || "EUR"}
            </div>
          )}
          {!isProduct && summary.note && (
            <div className="text-[12px] mt-1 text-espresso/70 line-clamp-2">{summary.note}</div>
          )}
        </div>
        <ChevronRight size={16} style={{ color: "#8B6F4B", flexShrink: 0, marginTop: 4 }} />
      </div>
      <div className="text-[11.5px] mt-3 tabular-nums" style={{ color: "#B9A692" }}>
        Обновлено {fmtDate(summary.updated_at)}
      </div>
    </button>
  );
}

function WorkbookDetailDrawer({ state, onClose, onDeleted }) {
  const entry = state?.entry;
  const payload = entry?.payload || {};
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!entry?.id) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm("Удалить эту запись? Восстановить её будет нельзя.");
    if (!ok) return;
    setDeleting(true);
    try {
      await deleteWorkbookEntry(entry.id);
      toast.success("Запись удалена");
      onDeleted?.(entry.id);
    } catch (e) {
      toast.error("Не удалось удалить запись");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
         style={{ background: "rgba(35,23,16,0.5)" }}
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
         data-testid="wb-detail">
      <div className="w-full md:max-w-2xl bg-[#FBF5EA] rounded-t-3xl md:rounded-3xl flex flex-col"
           style={{ boxShadow: "0 -8px 32px rgba(35,23,16,0.15)", maxHeight: "92dvh" }}>
        <div className="flex-shrink-0 p-5 md:p-6 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10.5px] tracking-[0.18em] uppercase mb-1" style={{ color: "#8B6F4B" }}>
                {entry?.type === "product_cost_calculation" ? "Расчёт изделия" : "Рецепт · Расчёт"}
              </div>
              <div className="text-[20px] leading-tight break-words"
                   style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}>
                {payload.title || entry?.title || "Запись"}
              </div>
            </div>
            <button onClick={onClose}
                    className="w-8 h-8 rounded-full grid place-items-center flex-shrink-0"
                    style={{ background: "#F7F1E7", color: "#4A3728", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    aria-label="Закрыть" data-testid="wb-detail-close">
              <X size={13} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 px-5 md:px-6 pb-4 overflow-y-auto space-y-4"
             style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
          {state?.loading && (
            <div className="text-sm text-espresso/70">Загружаем…</div>
          )}

          {entry && entry.type === "product_cost_calculation" && (
            <ProductCostReadOnly payload={payload} />
          )}

          {entry && entry.type !== "product_cost_calculation" && (
            <>
              {(shapeSummary(payload) || heightHint(payload) || payload?.target_shape?.count) && (
                <Section title="Форма">
                  {shapeSummary(payload) && <Row label="Схема" value={shapeSummary(payload)} />}
                  {heightHint(payload) && <Row label="Высота / толщина" value={heightHint(payload)} />}
                  {payload?.target_shape?.count && (
                    <Row label="Количество форм" value={fmtNum(payload.target_shape.count, 0)} />
                  )}
                </Section>
              )}

              {payload.source_ingredients?.length > 0 && (
                <Section title="Исходные ингредиенты">
                  <IngredientList items={payload.source_ingredients} testid="wb-source" />
                </Section>
              )}

              {payload.recalculated_ingredients?.length > 0 && (
                <Section title="Пересчитанные ингредиенты">
                  <IngredientList items={payload.recalculated_ingredients} testid="wb-recalc" />
                </Section>
              )}

              {payload.technique && (
                <Section title="Технология приготовления">
                  <div className="text-[13.5px] whitespace-pre-wrap leading-relaxed"
                       style={{ color: "#2A1D14" }} data-testid="wb-technique">
                    {payload.technique}
                  </div>
                </Section>
              )}

              {payload.cost && (
                <Section title="Себестоимость ингредиентов">
                  <div className="rounded-xl p-3 bg-white" style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
                    {payload.cost.complete ? (
                      <div className="flex items-baseline justify-between">
                        <div className="text-[13px]" style={{ color: "#5B4326" }}>Итого</div>
                        <div className="text-[18px] tabular-nums"
                             style={{ color: "#2A1D14", fontFamily: "'Cormorant Garamond', serif" }}
                             data-testid="wb-cost-total">
                          {fmtMoney(payload.cost.full_total)} {payload.cost.currency_code || payload.currency_code}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="text-[13px]" style={{ color: "#5B4326" }}>
                          Рассчитано: <span className="tabular-nums">{fmtMoney(payload.cost.partial_total)} {payload.cost.currency_code || payload.currency_code}</span>
                        </div>
                        {payload.cost.missing_count > 0 && (
                          <div className="text-[12px] mt-0.5" style={{ color: "#B26B4A" }}>
                            Не хватает данных: {payload.cost.missing_count}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {(payload.currency_code || payload.cost?.currency_code) && (
                <Section title="Валюта">
                  <div className="text-[13.5px]" style={{ color: "#2A1D14" }} data-testid="wb-currency">
                    {payload.currency_code || payload.cost?.currency_code}
                  </div>
                </Section>
              )}

              {entry.note && (
                <Section title="Личная заметка">
                  <div className="text-[13.5px] whitespace-pre-wrap" style={{ color: "#2A1D14" }} data-testid="wb-note">
                    {entry.note}
                  </div>
                </Section>
              )}
            </>
          )}

          {entry && (
            <>
              <div className="text-[11.5px] pt-1" style={{ color: "#B9A692" }}>
                Создано {fmtDate(entry.created_at)} · Обновлено {fmtDate(entry.updated_at)}
              </div>

              <div className="pt-2">
                <button onClick={handleDelete}
                        disabled={deleting}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[12.5px] disabled:opacity-60"
                        style={{ background: "transparent", color: "#B26B4A", boxShadow: "inset 0 0 0 1px #E7C9B7" }}
                        data-testid="wb-delete">
                  <Trash2 size={13} />
                  {deleting ? "Удаление…" : "Удалить"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1.5" style={{ color: "#8B6F4B" }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between text-[13px] py-1">
      <span style={{ color: "#8B6F4B" }}>{label}</span>
      <span style={{ color: "#2A1D14" }}>{value}</span>
    </div>
  );
}

function IngredientList({ items, testid }) {
  return (
    <div className="rounded-xl bg-white divide-y divide-[#EFE4D0]"
         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="flex items-baseline justify-between px-3 py-2 text-[13px] gap-3">
          <span className="truncate" style={{ color: "#2A1D14" }}>{it.name}</span>
          <span className="tabular-nums flex-shrink-0" style={{ color: "#5B4326" }}>
            {it.amount != null ? `${fmtNum(it.amount)} ${it.unit || ""}`.trim() : (it.unit || "—")}
          </span>
        </div>
      ))}
    </div>
  );
}

/* Read-only view of a product_cost_calculation payload inside the Cabinet.
 * Deliberately does NOT reuse MOProductCost editor — this component belongs
 * to «Мой кабинет → Рабочая тетрадь» and works even if the user has no
 * Master Order access. Numbers are formatted for display only; nothing
 * is recomputed here (values come from the client that saved the entry).
 */
function ProductCostReadOnly({ payload }) {
  const cur = payload?.currency_code || "EUR";
  const money = (v) => Number.isFinite(Number(v)) ? `${fmtNum(v)} ${cur}` : "—";
  const compTotal = (c) => {
    const b = Number(c?.base_cost);
    const k = Number(c?.coefficient);
    if (!Number.isFinite(b) || !Number.isFinite(k) || k <= 0) return null;
    return b * k;
  };
  const pkgTotal = (r) => {
    const u = Number(r?.unit_cost);
    const q = Number(r?.quantity);
    if (!Number.isFinite(u) || !Number.isFinite(q) || q <= 0) return null;
    return u * q;
  };
  const numOrNull = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const laborHrs = numOrNull(payload?.labor_hours);
  const laborRate = numOrNull(payload?.hourly_rate);
  const laborTot = numOrNull(payload?.labor_total);
  const laborShown = laborTot !== null || (laborHrs !== null && laborRate !== null);

  return (
    <>
      {payload?.components?.length > 0 && (
        <Section title="Компоненты изделия">
          <div className="rounded-xl bg-white divide-y divide-[#EFE4D0]"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="wb-pc-components">
            {payload.components.map((c, i) => {
              const total = compTotal(c);
              return (
                <div key={i} className="px-3 py-2 text-[13px]">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="truncate" style={{ color: "#2A1D14" }}>{c.name}</span>
                    <span className="tabular-nums flex-shrink-0" style={{ color: "#2A1D14" }}>
                      {total === null ? "—" : money(total)}
                    </span>
                  </div>
                  <div className="text-[11.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
                    База {money(c.base_cost)} · ×{fmtNum(c.coefficient)}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {payload?.packaging_consumables?.length > 0 && (
        <Section title="Упаковка и расходники">
          <div className="rounded-xl bg-white divide-y divide-[#EFE4D0]"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="wb-pc-packaging">
            {payload.packaging_consumables.map((r, i) => {
              const total = pkgTotal(r);
              return (
                <div key={i} className="px-3 py-2 text-[13px]">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="truncate" style={{ color: "#2A1D14" }}>{r.name}</span>
                    <span className="tabular-nums flex-shrink-0" style={{ color: "#2A1D14" }}>
                      {total === null ? "—" : money(total)}
                    </span>
                  </div>
                  <div className="text-[11.5px] mt-0.5" style={{ color: "#8B6F4B" }}>
                    {fmtNum(r.quantity)} {r.unit} · {money(r.unit_cost)}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      <Section title="Труд">
        {laborShown ? (
          <div className="rounded-xl bg-white p-3 text-[13px]"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="wb-pc-labor">
            <Row label="Часы" value={fmtNum(payload.labor_hours)} />
            <Row label={`Ставка / час, ${cur}`} value={fmtNum(payload.hourly_rate)} />
            <Row label="Итог по труду" value={money(payload.labor_total)} />
          </div>
        ) : (
          <div className="text-[13px]" style={{ color: "#8B6F4B" }} data-testid="wb-pc-labor-empty">
            Труд не включён в расчёт
          </div>
        )}
      </Section>

      {payload?.additional_expenses?.length > 0 && (
        <Section title="Дополнительные расходы">
          <div className="rounded-xl bg-white divide-y divide-[#EFE4D0]"
               style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="wb-pc-expenses">
            {payload.additional_expenses.map((e, i) => {
              const hasAmount = e?.amount !== null && e?.amount !== undefined && e?.amount !== ""
                                && Number.isFinite(Number(e.amount));
              return (
                <div key={i} className="flex items-baseline justify-between px-3 py-2 text-[13px] gap-3">
                  <span className="truncate" style={{ color: "#2A1D14" }}>{e.name || "Расход"}</span>
                  <span className="tabular-nums flex-shrink-0"
                        style={{ color: hasAmount ? "#2A1D14" : "#B9A692" }}>
                    {hasAmount ? money(e.amount) : "—"}
                  </span>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      <Section title="Итоги">
        <div className="rounded-xl bg-white p-3 text-[13px]"
             style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }} data-testid="wb-pc-summary">
          <Row label="Себестоимость компонентов" value={money(payload.subtotal)} />
          <Row label="Упаковка и расходники" value={money(payload.packaging_consumables_subtotal)} />
          <Row label="Труд" value={laborTot !== null ? money(laborTot) : "—"} />
          <Row label="Дополнительные расходы" value={money(payload.additional_expenses_subtotal)} />
          <div className="mt-2 pt-2 border-t border-[#EFE4D0] flex items-baseline justify-between">
            <span className="text-[13px]" style={{ color: "#5B4326" }}>Полная себестоимость изделия</span>
            <span className="text-[16px] tabular-nums"
                  style={{ color: "#2A1D14", fontFamily: "'Cormorant Garamond', serif" }}
                  data-testid="wb-pc-full-total">
              {money(payload.full_cost_total ?? payload.grand_total ?? payload.subtotal)}
            </span>
          </div>
          {(() => {
            const salePriceN = numOrNull(payload?.sale_price);
            const profitN = numOrNull(payload?.profit);
            const marginN = numOrNull(payload?.margin_percent);
            if (salePriceN === null && profitN === null && marginN === null) return null;
            return (
              <div className="mt-2 pt-2 border-t border-[#EFE4D0]" data-testid="wb-pc-sale-block">
                <div className="flex items-baseline justify-between py-1">
                  <span style={{ color: "#5B4326" }}>Цена продажи</span>
                  <span className="tabular-nums"
                        style={{ color: salePriceN === null ? "#B9A692" : "#2A1D14" }}
                        data-testid="wb-pc-sale-price">
                    {salePriceN === null ? "—" : money(salePriceN)}
                  </span>
                </div>
                <div className="flex items-baseline justify-between py-1">
                  <span style={{ color: "#5B4326" }}>Прибыль с изделия</span>
                  <span className="tabular-nums"
                        style={{
                          color: profitN === null ? "#B9A692"
                               : profitN < 0 ? "#B26B4A" : "#2A6B48",
                        }}
                        data-testid="wb-pc-profit">
                    {profitN === null ? "—" : money(profitN)}
                  </span>
                </div>
                <div className="flex items-baseline justify-between py-1">
                  <span style={{ color: "#5B4326" }}>Маржа</span>
                  <span className="tabular-nums"
                        style={{
                          color: marginN === null ? "#B9A692"
                               : marginN < 0 ? "#B26B4A" : "#2A6B48",
                        }}
                        data-testid="wb-pc-margin">
                    {marginN === null ? "—" : `${fmtNum(marginN)}%`}
                  </span>
                </div>
              </div>
            );
          })()}
        </div>
      </Section>

      <Section title="Валюта">
        <div className="text-[13.5px]" style={{ color: "#2A1D14" }} data-testid="wb-pc-currency">{cur}</div>
      </Section>
    </>
  );
}

